import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  MapPinIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserIcon,
  PhoneIcon,
  TruckIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { fetchMedicineOrderDetail } from '../../../services/MasterAdmin/medicalStoreService';
import type { User } from '../../../models/MasterAdmin/user';
import { format } from 'date-fns';

type MedicineLine = {
  orderMedicineId?: string;
  medicineName?: string;
  quantity?: number;
  price?: number;
  totalAmount?: number;
};

type DeliveryAddr = {
  formattedAddress?: string;
  houseStreet?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

type OrderDetail = {
  orderId?: string;
  orderStatus?: string;
  paymentStatus?: string;
  status?: string;
  User?: User | null;
  user?: User | null;
  patientName?: string | null;
  doctorName?: string | null;
  subtotal?: number;
  deliveryCharges?: number;
  totalAmount?: number;
  paymentStatusRaw?: string;
  deliveryType?: string | null;
  borzoCourierName?: string | null;
  borzoCourierPhone?: string | null;
  cancellationReason?: string | null;
  cancelledBy?: string | null;
  createdAt?: string;
  deliveryCompletedAt?: string | null;
  medicines?: MedicineLine[];
  deliveryAddress?: DeliveryAddr | null;
  prescription?: { prescriptionId?: string } | null;
};

function getCustomer(order: OrderDetail) {
  const u = order.User ?? order.user;
  return {
    name: u?.name ?? order.patientName ?? '—',
    phone: u?.phone_number ?? '—',
    email: u?.emailId,
  };
}

const MedicalStoreMedicineOrderDetailPage: React.FC = () => {
  const { vendorId, orderId } = useParams<{ vendorId: string; orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!vendorId || !orderId) return;
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchMedicineOrderDetail(vendorId, orderId);
        setOrder(data as OrderDetail);
      } catch {
        setError('Failed to load order details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [vendorId, orderId]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error || 'Order not found'}</p>
        <button
          type="button"
          onClick={() => navigate(`/vendors/medical-store/${vendorId}`)}
          className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
        >
          Back to vendor dashboard
        </button>
      </div>
    );
  }

  const cust = getCustomer(order);
  const addr = order.deliveryAddress;
  const addressLine =
    addr?.formattedAddress ||
    [addr?.houseStreet, addr?.addressLine1, addr?.city, addr?.state, addr?.zipCode]
      .filter(Boolean)
      .join(', ') ||
    '—';

  const lines = Array.isArray(order.medicines) ? order.medicines : [];

  return (
    <div className="w-full min-w-0 max-w-4xl mx-auto p-3 sm:p-4 md:p-6">
      <div className="mb-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(`/vendors/medical-store/${vendorId}`)}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to vendor dashboard
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b dark:border-gray-700 flex items-center justify-end flex-wrap gap-2">
          <span
            className={`px-3 py-1 text-sm font-medium rounded-full ${
              order.orderStatus === 'Delivered'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                : order.orderStatus === 'Cancelled'
                ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
            }`}
          >
            {order.orderStatus ?? '—'}
          </span>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Order ID
            </p>
            <p className="mt-1 text-sm font-mono text-gray-900 dark:text-white break-all">
              {order.orderId ?? '—'}
            </p>
            {order.prescription?.prescriptionId && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Prescription ID: {order.prescription.prescriptionId}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                <UserIcon className="h-4 w-4 mr-2" />
                Customer
              </h3>
              <p className="text-gray-900 dark:text-white">{cust.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center mt-1">
                <PhoneIcon className="h-4 w-4 mr-1" />
                {cust.phone}
              </p>
              {cust.email ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{cust.email}</p>
              ) : null}
              {(order.patientName || order.doctorName) && (
                <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                  {order.patientName && <p>Patient: {order.patientName}</p>}
                  {order.doctorName && <p>Doctor: {order.doctorName}</p>}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                Amounts & payment
              </h3>
              <p className="text-gray-900 dark:text-white">
                Subtotal: ₹{Number(order.subtotal ?? 0).toFixed(2)}
              </p>
              <p className="text-gray-900 dark:text-white mt-1">
                Delivery: ₹{Number(order.deliveryCharges ?? 0).toFixed(2)}
              </p>
              <p className="text-gray-900 dark:text-white mt-1 font-medium">
                Total: ₹{Number(order.totalAmount ?? 0).toFixed(2)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Payment:{' '}
                <span
                  className={`font-medium ${
                    order.paymentStatus === 'Paid'
                      ? 'text-green-600 dark:text-green-400'
                      : order.paymentStatus === 'Failed'
                      ? 'text-red-600 dark:text-red-400'
                      : ''
                  }`}
                >
                  {order.paymentStatus ?? '—'}
                </span>
              </p>
              {order.deliveryType && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Delivery: {String(order.deliveryType).replace(/_/g, ' ')}
                </p>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
              <MapPinIcon className="h-4 w-4 mr-2" />
              Delivery address
            </h3>
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-gray-900 dark:text-white">{addressLine}</p>
            </div>
          </div>

          {(order.borzoCourierName || order.borzoCourierPhone) && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                <TruckIcon className="h-4 w-4 mr-2" />
                Delivery partner
              </h3>
              <p className="text-gray-900 dark:text-white">{order.borzoCourierName ?? '—'}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{order.borzoCourierPhone ?? '—'}</p>
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
              <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
              Line items
            </h3>
            {lines.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No line items.</p>
            ) : (
              <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="text-left px-3 py-2 text-gray-600 dark:text-gray-300">Medicine</th>
                      <th className="text-right px-3 py-2 text-gray-600 dark:text-gray-300">Qty</th>
                      <th className="text-right px-3 py-2 text-gray-600 dark:text-gray-300">Price</th>
                      <th className="text-right px-3 py-2 text-gray-600 dark:text-gray-300">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {lines.map((m) => (
                      <tr key={m.orderMedicineId ?? m.medicineName}>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">{m.medicineName ?? '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                          {m.quantity ?? 0}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                          ₹{Number(m.price ?? 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-900 dark:text-white">
                          ₹{Number(m.totalAmount ?? 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center">
              <CalendarIcon className="h-4 w-4 mr-2" />
              Created:{' '}
              {order.createdAt ? format(new Date(order.createdAt), 'dd MMM yyyy HH:mm') : '—'}
            </div>
            {order.deliveryCompletedAt && (
              <div className="flex items-center">
                <CalendarIcon className="h-4 w-4 mr-2" />
                Delivered: {format(new Date(order.deliveryCompletedAt), 'dd MMM yyyy HH:mm')}
              </div>
            )}
          </div>

          {(order.cancellationReason || order.cancelledBy) && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-300">
                Cancelled by: {String(order.cancelledBy ?? '—')}
              </p>
              {order.cancellationReason ? (
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                  Reason: {String(order.cancellationReason)}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicalStoreMedicineOrderDetailPage;
